define((require) => {
    "use strict";

    const {
        first, extend, map, escape
    }  = require("underscore");
    const Browser                       = require("shared/services/Browser");
    const GroupModel                    = require("shared/ui/groups/GroupModel");
    const GroupUtil                     = require("ui/groups/GroupUtil");
    const Marionette                    = require("marionette");
    const MessageTypes                  = require("ui/publish/MessageTypes");
    const SceneThumbnailViewer          = require("ui/viewer/SceneThumbnailViewer");
    const Size                          = require("shared/util/Size");
    const ThumbnailLayouter             = require("util/ThumbnailLayouter");
    const Tooltip                       = require("ui/common/Tooltip");
    const draggableContentTemplate      = require("ui/publish/DraggableContentThumbnailView.tpl");
    const {Logger}                        = require("shared/util/Logger");

    const CURRENT_SCHEDULED_DATE = "now";
    const MAX_THUMB_DIMENSION = {width: 120, height: 90};
    const MAX_VARIANT_THUMB_DIMENSION = {width: 77, height: 80};

    const log = new Logger("ExperienceCollectionThumbnailView");

    return Marionette.Layout.extend({

        className: "draggable-experience-thumbnail",

        template: draggableContentTemplate,

        templateHelpers() {
            // Extract locale string in order to render associated flag - see DraggableContentThumbnailView.tpl
            var isLocalized = !!this.options.localizedGroup,
                localeString;

            if (isLocalized) {
                var entry = this.options.localizedGroup.findEntryByAssetId(this.model.get("id"));
                localeString = entry.get("value").get("localeString");
            }

            return {
                allowedToPublish: !this.options.notAllowedToPublish,
                isLocalized: isLocalized,
                localeString: localeString,
                isBreakpointGroup: !!this._group,
                thumbs: this._isGroupOneUnit && this._getThumbs()
            };
        },

        _scheduledDate: CURRENT_SCHEDULED_DATE,

        _tooltip: false,

        _timer: null,

        /**
         * The data payload sent during the drag operation, containing the thumbnail <tt>type</tt> and <tt>id</tt>.
         *
         * @type {Object}
         */
        _dragPayload: null,

        _element: null,

        _group: null,

        _variants: null,

        regions: {
            experienceThumbnailWrapper: ".experience-thumbnail-wrapper"
        },

        ui: {
            experienceThumbnailWrapper: ".experience-thumbnail-wrapper"
        },

        events: {
            "dragstart": function(event) {
                if (this.options.notAllowedToPublish) {
                    event.preventDefault();

                    return false;
                }

                this._hideTooltip();
                // Ensures that only the experience itself is used as the drag handle...
                var container = this.$el,
                    transfer = event.originalEvent.dataTransfer;

                if (transfer.setDragImage) { // problems in IE on cross-domain...
                    transfer.setDragImage(container.get(0), container.width() / 2, container.height() / 2);
                }
                // We *must* set data as part of the drag start event, or Firefox will not allow drag at all. However,
                // we no longer use the data set (id) in dropPage.html, since Firefox will not allow the
                // dropPage.html to read the id if the customer site is on https and the editor on http (or
                // vice versa). The id and type are now passed to the dropPage.html as a search parameter in the
                // iframe url. Also, Firefox must not use the "Text" as the data tag or it will try and open it as
                // a link...
                transfer.setData(Browser.type.firefox ? "ContentId" : "Text", this._dragPayload.type + ":" + this._dragPayload.id);

                this.options.node.post({action: MessageTypes.DRAG_START, data: this._dragPayload});
            },

            "selectstart": function(event) { // Required for IE9 to drag anything other than IMG and A
                if (!this.options.notAllowedToPublish) {
                    event.target.dragDrop();
                }
            },

            "dragend": function() {
                if (!this.options.notAllowedToPublish) {
                    this.options.node.post({action: MessageTypes.DRAG_END, data: this._dragPayload});
                }
            }
        },

        initialize() {
            var id = this.model.get("id");

            if (this.model instanceof GroupModel) {
                this._group = this.model;
                this.model = this._group.getAssets()[0];
                this._isGroupOneUnit = this._group.isOneUnit();
                this._variants = this._isGroupOneUnit && this._getVariants();
            }

            this._dragPayload = {
                id: id,
                type: this._group ? "group" : "experience"
            };
        },

        onShow() {
            var thumbnail = new SceneThumbnailViewer({
                model: this.model.get("scenes").at(0),
                experience: this.model,
                maxDimensions: this._group ? MAX_VARIANT_THUMB_DIMENSION : MAX_THUMB_DIMENSION,
                showStaticScenes: true
            });

            this.experienceThumbnailWrapper.show(thumbnail);

            this._addTooltip();
        },

        _getVariants() {
            return GroupUtil.breakpointRangeAssets(this._group);
        },

        _getThumbs() {
            var primaryModel = first(this._variants),
                dimensions = map(this._variants, (variant) => {
                    return {
                        width: variant.get("width"),
                        height: variant.get("height")
                    };
                }),

                layouts;

            if (primaryModel && primaryModel.get("backdrop").isFitContainer()) {
                var firstDimension = dimensions[0],
                    size = new Size(firstDimension.width, firstDimension.height),
                    scaledSize = size.shrinkToAspectRatio(MAX_VARIANT_THUMB_DIMENSION.width / MAX_VARIANT_THUMB_DIMENSION.height);

                extend(firstDimension, scaledSize);
            }

            layouts = ThumbnailLayouter.calculateLayout(dimensions, {
                containerWidth: 110,
                fitboxDimensions: {
                    width: MAX_VARIANT_THUMB_DIMENSION.width,
                    height: MAX_VARIANT_THUMB_DIMENSION.height
                }
            });

            return layouts;
        },

        _findSlots() {
            const id = this.model.get("id");
            const channels = this.options.channelConfig.get("channels");
            const slotsInfoList = [];
            let state = this.model.get("state");

            channels.forEach((channelJson) => {
                const typeKey = this._group ? "group" : "experience";
                const publishable = channelJson[typeKey];

                if (publishable && publishable.id === id) {
                    const {name, url} = channelJson;

                    slotsInfoList.push("<b>" + name + "</b>" + (url ? " &ndash; " + url : ""));
                    // State the same for all channels, computed from the backend...
                    state = publishable.state;
                }
            });

            slotsInfoList.sort();

            return {slots: slotsInfoList, state: state};
        },

        describeGroup(group) {
            var s = "Breakpoint Intervals: <ol class='ranges'>";

            group.forEachRange((range, experience) => {
                s += "<li class='word-break'>";

                if (experience) {
                    s += "<b>" + escape(experience.distinguishedName()) + "</b>"; // will include debug (id/version) info where relevant
                } else { // should never happen
                    s += "[" + escape(experience.resourceId) + "]"; // mimic id format
                }

                s += " &ndash; " + range;
                s += "</li>";
            });

            return s + "</ol>";
        },

        _hideTooltip() {
            if (this._timer) {
                clearTimeout(this._timer);
            }

            this._tooltip = false;
            Tooltip.hide(this._element);
        },

        /**
         * Visible for testing
         * @returns {string} HTML to be put inside the tooltip
         */
        buildTooltipHtml() {
            var entry = this._findSlots(),
                escapedEntryState = escape(entry.state),
                titleHtml = "<p><b>" + escape(this._group ? this._group.publishableDisplayName() : this.model.publishableDisplayName()) + "</b>",
                stateHtml = "<p>Status: " + escapedEntryState,
                escapedVersion = escape(Math.abs(this.model.get("version"))),
                escapedPushedVersion = escape(this.model.get("pushedVersion")),
                changedSinceLastPush = this.model.has("changedSinceLastPush") && this.model.get("changedSinceLastPush"),
                pushedVersionUnknown = this.model.has("pushedVersionUnknown") && this.model.get("pushedVersionUnknown"),
                groupMsg = "",
                slotsHtml;

                // We cannot used distinguishedName, since localization handled explicitly below...
            if (Logger.level() >= Logger.VERBOSE) {
                titleHtml += "<br>[" + this.model.get("id") + ", #" + escapedVersion + "]";
                stateHtml += " [#" + escapedPushedVersion + "]";
                stateHtml += " [changedSinceLastPush: " + changedSinceLastPush + "]";
                stateHtml += " [pushedVersionUnknown: " + pushedVersionUnknown + "]";
            }

            titleHtml += "</p>";

            if (changedSinceLastPush) {
                stateHtml += ", but latest version not pushed";
            }

            if (pushedVersionUnknown) {
                stateHtml += " - <i>the " + escapedEntryState + " version cannot be shown here until next push; this is the latest saved version.</i>";
            }

            stateHtml += "</p>";

            if (this._group) {
                groupMsg = "<p>" + this.describeGroup(this._group) + "</p>";
            } else if (this.model.groupType) {
                groupMsg = "<p>Localization: " + this.model.identifier + "</p>";
            }

            if (entry.slots.length > 1) {
                slotsHtml = "<p>Currently used in " + entry.slots.length + " Slots:</p>";
                slotsHtml += "<ul><li class='word-break'>" + entry.slots.join("</li><li class='word-break'>") + "</li></ul>";
            } else if (entry.slots.length === 1) {
                slotsHtml = "<p class='word-break'>Slot: \"" + entry.slots[0] + "\"</p>";
            } else {
                slotsHtml = "<p>Not used in any Slots.</p>";
            }

            return titleHtml + stateHtml + groupMsg + slotsHtml;
        },

        _addTooltip() {
            this._element = this.$el.find(".scene-wrapper-outer");

            Tooltip.add(this._element, {
                content: this.buildTooltipHtml(),
                trigger: "manual", // hover with delay and click with no delay at the same time not supported by default!
                width: 220,
                duration: 10000, // auto hide after 10 seconds, may be a lot of text to read for the user
                html: true
            });

            this._element.on("mouseenter", () => {
                this._timer = setTimeout(() => {
                    if (!this._tooltip) {
                        this._tooltip = true;
                        Tooltip.show(this._element);
                    }
                }, 1000);
            });

            this._element.on("click", () => {
                if (this._timer) {
                    clearTimeout(this._timer);
                }

                if (this._tooltip) {
                    Tooltip.hide(this._element);
                } else {
                    Tooltip.show(this._element);
                    log.verbose("Experience clicked: " + this.model.get("id"), this.model);
                }

                this._tooltip = !this._tooltip;
            });

            this._element.on("mouseleave", () => this._hideTooltip());
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/publish/DraggableContentThumbnailView.js